// logout.js
'use strict';
import { clearState, PERSIST_TIMEOUT_MS } from '../resource/persist.js';
import Delay from '../resource/Delay.js';
import { LOGOUT, PERSIST_NOW } from '../ActionTypes.js';
import { processMixpanelApiCall } from '../resource/mixpanel.js';
import { push as customerServicePush } from '../resource/customerService.js';
import getConfigData from '../selector/getConfigData.js';
import getMeData from '../selector/getMeData.js';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';
import { sendMessageToSW } from '../serviceWorker/helpers.js';
import { replace } from '../action/navigationAction.js';
import resetConfigurations from '../action/resetConfigurations.js';
import updatePusherDistinctId from '../action/updatePusherDistinctId.js';
import { updateGrowthBookAttribute } from '../action/configActions.js';
import { setMixpanelViewIdMeData } from '../resource/getMixpanelViewId.js';

const hasCachesApi = typeof caches !== 'undefined';
const privateCacheKeys = ['message-resource', 'general-api'];

// Bound IndexedDB-backed work so a stalled storage engine (iOS Safari) can't
// hang the await forever and block the logout redirect; on timeout we just let
// logout proceed.
const withTimeout = promise =>
  Promise.race([promise, new Delay(PERSIST_TIMEOUT_MS).promise()]);

/**
 * Logout
 * @kind action
 * @param {string} [{pathname}='/'] - redirect path after logout
 * @param {string} [{search}='/'] - redirect search after logout
 * @param {boolean} [{isFromServiceWorker}=false] - if from service worker
 * @return {Promise} Action promise
 */
const logout =
  ({ pathname = '/', search = '', isFromServiceWorker = false } = {}) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    if (!token) {
      return dispatch({ type: '' });
    }

    // Delete FCM token
    const fcmToken = getMeData(getState(), 'fcmToken');
    if (fcmToken) {
      import('../resource/fcm.js')
        .then(({ deleteFcmToken }) => deleteFcmToken())
        .catch(() => null);
    }

    // Reset ServiceWorker dynamic caches.
    if (hasCachesApi) {
      caches
        .keys()
        .then(keys =>
          Promise.all(
            keys
              .filter(key => privateCacheKeys.includes(key))
              .map(key => caches.delete(key))
          )
        )
        .catch(() => null);
    }

    // for latter might use
    await withTimeout(clearState());
    dispatch({ type: LOGOUT });

    setMixpanelViewIdMeData({
      meId: undefined,
      meUsername: undefined,
    });

    customerServicePush({ args: ['logoutUser'] });

    dispatch(resetConfigurations());

    // Reset GrowthBook to client config (reducer preserves attribute.client on LOGOUT)
    const clientAttribute = getConfigData(getState(), ['attribute', 'client']);
    await dispatch(updateGrowthBookAttribute({ attribute: clientAttribute }));

    if (!isFromServiceWorker) {
      // Reset mixpanel so logout mints a fresh anonymous $device_id, isolating
      // the next account's pre-login events instead of merging them onto the
      // first account that claimed this device.
      processMixpanelApiCall({ apiNames: ['reset'] });
      // Sync the reset $device_id into the X-Track header, so backend-attributed
      // events (e.g. SMS) stop using the previous account's distinct id.
      await dispatch(updatePusherDistinctId());

      await withTimeout(dispatch({ type: PERSIST_NOW }));
      // only local logout action should post message to SW then clear event, channel.
      // if from SW means some tab logout action was called.
      sendMessageToSW({ type: MessageTypes.LOGOUT });
    }
    return dispatch(replace({ pathname, search }));
  };

export default logout;
